<template>
  <div
    class="flex h-24 w-full flex-col justify-between justify-self-center rounded-sm border border-gray-200 p-4"
  >
    <div
      class="flex items-center justify-between text-xs font-semibold text-headline"
    >
      <span class="w-50 overflow-x-hidden text-ellipsis whitespace-nowrap">
        {{ scorecard.name }}
      </span>
      <div v-if="selectedScorecard" class="flex items-center justify-between">
        <span
          v-tooltip="
            isReport && group
              ? $t('SCORECARD_GROUPS.GPTS', { points: groupPoints })
              : null
          "
          class="mr-3"
        >
          {{ $t("SCORECARDS.PTS", { points }) }}
        </span>
        <scorecard-risk-tier-pill
          v-if="!pageIsView && !!group?.results?.length"
          class="mr-3"
          reduce
          font-size="text-xxs"
          :risk-tier="
            'score_card_risk_tier' in selectedScorecard
              ? selectedScorecard.score_card_risk_tier
              : null
          "
          :readonly="true"
        />
        <lf-pill
          v-if="
            !pageIsView &&
            !!group?.results?.length &&
            'score_card_status' in selectedScorecard
          "
          :color-index="
            getColorId(selectedScorecard.score_card_status?.color || '')
          "
        >
          {{ selectedScorecard?.score_card_status?.name || "--" }}
        </lf-pill>
      </div>
    </div>
    <scorecard-points-bar
      v-if="pageIsView"
      :categories="scorecard?.content?.categories"
      :total="scorecard?.points || 0"
      small
      tile-small
    />
    <scorecard-results-points-bar
      v-else
      :weighted-points="group?.results?.[0]?.weighted_points?.score_cards"
      :total="group?.group_points || 0"
      :started="!!group?.results?.length"
      is-group
      small
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from "vue";

import { getColorId } from "@/helpers/scorecards";
import { ScorecardsIsViewKey } from "@/helpers/symbols";

import ScorecardPointsBar from "./ScorecardPointsBar.vue";
import ScorecardResultsPointsBar from "./ScorecardResultsPointsBar.vue";
import ScorecardRiskTierPill from "./ScorecardRiskTierPill.vue";

import type { IScorecard, IScorecardGroup } from "@/models/scorecards";
import type { PropType } from "vue";

const props = defineProps({
  scorecard: {
    type: Object as PropType<IScorecard>,
    required: true,
  },
  group: {
    type: Object as PropType<IScorecardGroup | undefined>,
  },
  isReport: {
    type: Boolean,
    default: false,
  },
});

const pageIsView = inject(ScorecardsIsViewKey, true);

const selectedScorecard = computed(() => {
  if (props.isReport) {
    return props.scorecard.content;
  }
  return props.group?.results?.[0]?.score_card_results?.find(
    (s) => s.score_card_id === props.scorecard.id
  );
});

const points = computed(() => {
  if (pageIsView) {
    return props.scorecard.points || "-";
  }
  if (!selectedScorecard.value) {
    return "-";
  }
  let points;
  if (props.group) {
    points =
      props.group.results?.[0]?.weighted_points?.score_cards?.find(
        (s) => s.id === props.scorecard.id
      )?.weighted_points || 0;
  } else {
    points =
      "points" in selectedScorecard.value ? selectedScorecard.value?.points : 0;
  }
  const total = props.scorecard.group_weight ?? (props.scorecard.points || 1);
  return `${points}/${total}`;
});

const groupPoints = computed(() => {
  if (props.group) {
    return props.scorecard.group_weight;
  }
  return 0;
});
</script>
